import { useCallback, useEffect, useRef, useState } from 'react'
import { useAlertStore } from '../stores/alertStore'

interface NotificationOptions {
  body?: string
  icon?: string
  tag?: string
  requireInteraction?: boolean
}

interface UseNotificationsReturn {
  isSupported: boolean
  permission: NotificationPermission | 'unsupported'
  requestPermission: () => Promise<boolean>
  showNotification: (title: string, options?: NotificationOptions) => void
  playSound: () => void
}

// Generate a simple beep sound using Web Audio API
function createBeepSound(): () => void {
  return () => {
    try {
      const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.frequency.value = 880 // A5 note
      oscillator.type = 'sine'

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)

      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.3)
    } catch (err) {
      console.error('Failed to play beep:', err)
    }
  }
}

export function useNotifications(): UseNotificationsReturn {
  const { soundEnabled, browserNotificationsEnabled } = useAlertStore()
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('default')
  const playBeepRef = useRef<(() => void) | null>(null)

  const isSupported = typeof window !== 'undefined' && 'Notification' in window

  useEffect(() => {
    if (isSupported) {
      setPermission(Notification.permission)
    } else {
      setPermission('unsupported')
    }
  }, [isSupported])

  // Create beep sound function
  useEffect(() => {
    if (typeof window !== 'undefined') {
      playBeepRef.current = createBeepSound()
    }
  }, [])

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false

    try {
      const result = await Notification.requestPermission()
      setPermission(result)
      return result === 'granted'
    } catch (err) {
      console.error('Failed to request notification permission:', err)
      return false
    }
  }, [isSupported])

  const showNotification = useCallback(
    (title: string, options?: NotificationOptions) => {
      if (!isSupported || !browserNotificationsEnabled) return
      if (permission !== 'granted') return

      try {
        new Notification(title, {
          icon: '/favicon.svg',
          ...options,
        })
      } catch (err) {
        console.error('Failed to show notification:', err)
      }
    },
    [isSupported, browserNotificationsEnabled, permission]
  )

  const playSound = useCallback(() => {
    if (!soundEnabled) return

    try {
      if (playBeepRef.current) {
        playBeepRef.current()
      }
    } catch (err) {
      console.error('Failed to play sound:', err)
    }
  }, [soundEnabled])

  return {
    isSupported,
    permission,
    requestPermission,
    showNotification,
    playSound,
  }
}
